/**
 * Referral API
 *
 * RTK Query endpoints for referral code management and statistics.
 */

import { api } from '../api';

// Types
export type ReferralCodeType = 'general' | 'org' | 'coach';

export interface ReferralCodeInfo {
  id: string;
  code: string;
  type: ReferralCodeType;
  shareUrl: string;
  usedCount: number;
  maxUses: number | null;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
}

/** Referral counts. Referrals earn nothing in BeHeard (no rewards, no credits). */
export interface ReferralStats {
  totalReferrals: number;
  successfulReferrals: number;
  pendingReferrals: number;
  codes: ReferralCodeInfo[];
}

export interface Redemption {
  id: string;
  status: 'pending' | 'completed' | 'failed';
  redeemedAt: string;
  newUser: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface RedemptionsResponse {
  redemptions: Redemption[];
  total: number;
}

/** This month's completed referrals against the anti-abuse cap (nothing is earned). */
export interface ReferralLimits {
  referralsThisMonth: number;
  monthlyReferralCap: number;
}

export interface ValidateCodeResponse {
  valid: boolean;
  error?: string;
  type?: ReferralCodeType;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const referralApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get or create the user's default referral code
     */
    getMyReferralCode: builder.query<ReferralCodeInfo, void>({
      query: () => '/referrals/my-code',
      transformResponse: (response: ApiResponse<ReferralCodeInfo>) => response.data,
      providesTags: ['Referrals'],
    }),

    /**
     * Get referral statistics
     */
    getMyReferralStats: builder.query<ReferralStats, void>({
      query: () => '/referrals/stats',
      transformResponse: (response: ApiResponse<ReferralStats>) => response.data,
      providesTags: ['Referrals'],
    }),

    /**
     * Get list of redemptions
     */
    getMyRedemptions: builder.query<RedemptionsResponse, { limit?: number; offset?: number }>({
      query: ({ limit = 20, offset = 0 }) => `/referrals/redemptions?limit=${limit}&offset=${offset}`,
      transformResponse: (response: ApiResponse<RedemptionsResponse>) => response.data,
      providesTags: ['Referrals'],
    }),

    /**
     * Get referral limits (monthly caps)
     */
    getReferralLimits: builder.query<ReferralLimits, void>({
      query: () => '/referrals/limits',
      transformResponse: (response: ApiResponse<ReferralLimits>) => response.data,
      providesTags: ['Referrals'],
    }),
  }),
});

export const {
  useGetMyReferralCodeQuery,
  useGetMyReferralStatsQuery,
  useGetMyRedemptionsQuery,
  useGetReferralLimitsQuery,
} = referralApi;
